import { invariant, keyBy } from "@banjoanton/utils";
import type { EvaluationMessageScore } from "../engines/evaluator-engine";
import type { ChessGame } from "../models/chess-game-model";
import type { Move } from "../models/move-model";
import type { MoveRating } from "../models/move-rating-model";
import type { MultiPvLineSummary } from "../models/multi-pv-model";
import type { ChessPosition } from "../models/position-model";
import { MoveUtil } from "./move-util";
import { PositionUtil } from "./position-util";
import type { ChessColor } from "../models/base-model";
import { EvaluationComparerUtil } from "./evaluation-comparer-util";
import type { MoveAnnotation } from "../features/annotations/models/move-annotation";

type IsGreatMoveProps = {
    game: ChessGame;
    position: ChessPosition;
    move: Move;
    evalAfterCurrentMove: EvaluationMessageScore;
    bestLinesPreviousPosition?: MultiPvLineSummary;
};

// eslint-disable-next-line complexity
function isGreatMove({
    game,
    position,
    move,
    evalAfterCurrentMove,
    bestLinesPreviousPosition,
}: IsGreatMoveProps): boolean {
    if (position.index === 0) {
        return false;
    }

    const fenBeforeOurMove = position.previousFen ?? position.fen;
    const currentFen = position.fen;
    const moveUci = MoveUtil.buildUci(move.from, move.to);
    const absoluteEval = Math.abs(evalAfterCurrentMove.score ?? 0);

    if (MoveUtil.isKingEvasion(fenBeforeOurMove, moveUci)) {
        return false;
    }

    // Get previous/next positions from game
    const previousPosition =
        position.index > 0 ? game.positions[position.index - 1] : undefined;
    const positionBeforeOpponentsMove =
        position.index > 1 ? game.positions[position.index - 2] : undefined;
    const nextPosition = game.positions[position.index + 1];

    // Get moves for those positions
    const moveMap = keyBy(game.moves, (m) => m.id);
    const previousMove = previousPosition
        ? PositionUtil.getMoveForPosition(previousPosition, moveMap)
        : undefined;
    const nextMove = nextPosition
        ? PositionUtil.getMoveForPosition(nextPosition, moveMap)
        : undefined;
    const opponentsLastMoveUci = MoveUtil.getUciFromMove(previousMove);
    const opponentsNextMoveUci = MoveUtil.getUciFromMove(nextMove);

    if (MoveUtil.isOnlyLegalMove(fenBeforeOurMove)) {
        return false;
    }

    // not sure if this is correct, but it eliminates some false positives
    const isTakeBackOnSameSquare = previousMove?.to === move.to;
    if (isTakeBackOnSameSquare) {
        return false;
    }

    const isEqualPosition = absoluteEval < 140;

    if (
        isEqualPosition &&
        MoveUtil.isEvenMaterialTrade({
            fenBeforeOpponentsMove: positionBeforeOpponentsMove?.fen,
            fenBeforeOurMove,
            currentFen,
            opponentsLastMoveUci,
            moveUci,
            opponentsNextMoveUci,
        })
    ) {
        return false;
    }

    if (bestLinesPreviousPosition) {
        return MoveUtil.isOnlyGoodMove({
            lines: bestLinesPreviousPosition.lines,
            move,
            options: { equalityThresholdInCp: 70, gapThresholdInCp: 90 },
        });
    }

    return false;
}

type IsBrilliantMoveProps = {
    move: Move;
    annotations: MoveAnnotation[];
    evalBefore: EvaluationMessageScore;
    evalAfter: EvaluationMessageScore;
    centipawnLoss: number;
};

function isBrilliantMove({
    move,
    annotations,
    evalBefore,
    evalAfter,
    centipawnLoss,
}: IsBrilliantMoveProps): boolean {
    const sacrificeAnnotation = annotations.find(
        (ann) => ann.type === "sacrifice"
    );

    // Never brilliant on mate moves
    if (evalBefore.type === "mate") {
        return false;
    }

    if (!sacrificeAnnotation) {
        return false;
    }

    if (sacrificeAnnotation.sacrificedPiece === "p") {
        return false;
    }

    if (centipawnLoss > 45) {
        return false;
    }

    if (sacrificeAnnotation.evalImprovement < -50) {
        return false;
    }

    //  Not bad position after
    if (evalAfter.type === "cp") {
        const evalAfterFromPlayerPerspective =
            move.color === "w" ? evalAfter.score : -evalAfter.score;
        if (evalAfterFromPlayerPerspective < -100) {
            return false;
        }
    }

    //  Not completely winning before
    if (evalBefore.type === "cp") {
        const evalBeforeFromPlayerPerspective =
            move.color === "w" ? evalBefore.score : -evalBefore.score;
        if (evalBeforeFromPlayerPerspective > 750) {
            return false;
        }
    }

    return true;
}

type ClassifyMoveResponse = {
    rating: MoveRating;
    centipawnLoss: number;
};

export type ClassifyMoveEvaluations = {
    bestMove: string;
    evalAfterBestReply: EvaluationMessageScore;
    evalBefore: EvaluationMessageScore;
    evalAfter: EvaluationMessageScore;
    bestLinesPreviousPosition?: MultiPvLineSummary;
    bestLinesCurrentPosition: MultiPvLineSummary;
};

export type ClassifyMoveProps = {
    game: ChessGame;
    positionIndex: number;
    annotations: MoveAnnotation[];
} & ClassifyMoveEvaluations;

const getCentipawnLoss = (
    evalBefore: EvaluationMessageScore, // engine eval for BEST line (from the position before the move)
    evalAfterBestReply: EvaluationMessageScore, // engine eval for PLAYED line (after your move + best reply)
    moveColor: ChessColor
): number => {
    // Only defined for centipawn evaluations (cp). Handle mate separately if needed.
    if (evalBefore.type !== "cp" || evalAfterBestReply.type !== "cp") {
        return 0;
    }

    // Compare scores from the mover's perspective
    // Loss = how much worse the position became after the move + opponent's best reply
    const comparison = EvaluationComparerUtil.compareScore({
        oldScore: evalBefore,
        newScore: evalAfterBestReply,
        side: moveColor,
    });

    // Return the absolute loss (negative diff means position got worse)
    // Clamp to 0 since we only care about losses, not gains
    return Math.max(0, -comparison.diff);
};

// eslint-disable-next-line complexity
export const classifyMove = ({
    game,
    positionIndex,
    bestMove,
    evalAfterBestReply, // Evaluation AFTER user's move AND opponent's best reply
    evalBefore, // Evaluation BEFORE user's move
    evalAfter,
    bestLinesPreviousPosition,
    annotations,
}: ClassifyMoveProps): ClassifyMoveResponse => {
    const isBookMove = positionIndex <= game.opening.ply;
    const position = game.positions[positionIndex];

    invariant(position, "Position not found in game for classification");

    const moveMap = keyBy(game.moves, (m) => m.id);
    const move = PositionUtil.getMoveForPosition(position, moveMap);

    // no move should always mean first move, which is a book move
    if (isBookMove || !move) {
        return {
            centipawnLoss: 0,
            rating: "book",
        };
    }

    const centipawnLoss = getCentipawnLoss(
        evalBefore,
        evalAfterBestReply,
        move.color
    );

    if (
        isBrilliantMove({
            move,
            annotations,
            evalBefore,
            evalAfter,
            centipawnLoss,
        })
    ) {
        return {
            centipawnLoss,
            rating: "brilliant",
        };
    }

    if (
        isGreatMove({
            game,
            position,
            move,
            evalAfterCurrentMove: evalAfter,
            bestLinesPreviousPosition,
        })
    ) {
        return {
            centipawnLoss,
            rating: "great",
        };
    }

    const isBestMove = bestMove && bestMove === MoveUtil.getUciFromMove(move);
    if (isBestMove) {
        return {
            centipawnLoss,
            rating: "best",
        };
    }

    // TODO: Investigate potential mate detection issue:
    // This comparison uses evalBefore→evalAfter, but centipawnLoss uses evalBefore→evalAfterBestReply
    // If evalAfter is cp but evalAfterBestReply is mate, the mate transition won't be detected here.
    // Consider using evalAfterBestReply for both, or creating separate comparison objects.
    const comparison = EvaluationComparerUtil.compareScore({
        oldScore: evalBefore,
        newScore: evalAfter,
        side: move.color,
    });

    if (comparison.changeType === "cp-to-mate") {
        // If the position improved (or stayed roughly equal), the player is delivering mate
        if (comparison.result === "better" || comparison.result === "equal") {
            return { centipawnLoss: 0, rating: "excellent" };
        }

        // Position got worse - player is getting mated
        // Check how bad the position was before to determine rating
        // oldNorm is from player's perspective, so negative means losing
        // Thresholds based on conventional chess analysis standards (mimicking other engines):
        if (comparison.oldNorm < -850) {
            // Already losing badly (-850cp ≈ 8.5 pawns down)
            return { centipawnLoss, rating: "inaccuracy" };
        }

        if (comparison.oldNorm < -400) {
            // Losing moderately (-400cp ≈ 4 pawns down)
            return { centipawnLoss, rating: "miss" };
        }

        // The player who just moved is getting mated
        return { centipawnLoss, rating: "blunder" };
    }

    if (comparison.changeType === "mate-to-cp") {
        // If still winning by good centipawn score after losing mate, it's a miss
        // Still winning by significant margin (600cp ≈ 6 pawns up)
        const stillWinning = comparison.newNorm >= 600;

        if (stillWinning) {
            return { centipawnLoss, rating: "miss" };
        }

        // The player missed a forced mate
        return { centipawnLoss, rating: "blunder" };
    }

    if (comparison.changeType === "mate-to-mate") {
        // Improving mate: closer mate distance from player's perspective
        if (comparison.result === "better") {
            return { centipawnLoss: 0, rating: "excellent" };
        }

        // Worsening mate: further mate distance but still winning
        if (comparison.result === "worse" && comparison.newNorm > 0) {
            return { centipawnLoss: 0, rating: "good" };
        }

        // Losing mate: went from delivering mate to getting mated
        // (newNorm < 0 means losing from player's perspective)
        return { centipawnLoss: 0, rating: "blunder" };
    }

    let result: MoveRating;
    if (centipawnLoss <= 30) {
        result = "excellent";
    } else if (centipawnLoss <= 75) {
        result = "good";
    } else if (centipawnLoss <= 150) {
        result = "inaccuracy";
    } else if (centipawnLoss <= 300) {
        result = "mistake";
    } else {
        result = "blunder";
    }

    return {
        centipawnLoss,
        rating: result,
    };
};

export const EvaluationUtil = {
    classifyMove,
};
